/**
 * The camera connectivity check on the site page (ENG-2457):
 *
 *  - right after onboarding, once the first sync succeeds, the check is shown
 *    inline as the last onboarding step, refreshing while the connector
 *    starts and reports in;
 *  - otherwise a banner when any camera isn't streaming (prominent when none
 *    are), and a "Test cameras" button, both opening the full panel.
 *
 * For every customer, not only Camera Health Monitoring ones.
 */
import React from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { TsButton } from "../../Troubleshooting/TroubleshootingUi";
import { SiteConnectivityView } from "./SiteConnectivityView";
import { useSiteConnectivity } from "./useSiteConnectivity";
import {
  dryRunSupported,
  familyOf,
  summaryDetails,
  summarySentence,
  summaryTone,
} from "./connectivityModel";
import { finishOnboarding, getOnboardingEntry } from "./onboardingConnectivity";
import { WatchLiveButton } from "../WatchLive/WatchLiveButton";
import {
  DeviceSessionButton,
  ServerDeviceSessionButton,
} from "../DeviceSession/DeviceSessionButton";

/** How often the onboarding card re-reads the connector's report. */
export const ONBOARDING_REFRESH_MS = 30 * 1000;
/** …and for how long after the first sync. */
export const ONBOARDING_REFRESH_WINDOW_MS = 15 * 60 * 1000;

export const SiteConnectivityDialog = ({
  open,
  onClose,
  siteId,
  siteName,
  canRun,
  cameraIds,
  title,
  streamTestAvailable,
  deviceSessionAvailable,
}: {
  open: boolean;
  onClose: () => void;
  siteId: number;
  siteName?: string | null;
  canRun: boolean;
  cameraIds?: number[];
  title?: string;
  /** The site About's `stream_test_available`: a "Test stream" per camera. */
  streamTestAvailable?: boolean | null;
  /** The site About's `device_session_available`: "Open device" per camera. */
  deviceSessionAvailable?: boolean | null;
}) => (
  <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
    <DialogTitle
      sx={{
        fontFamily: "Mulish",
        fontWeight: 800,
        color: "#001943",
        pr: 6,
      }}
    >
      {title ?? "Camera check"}
      {siteName ? (
        <span className="block font-mulish font-normal text-[14px] text-[#4c5257]">
          {siteName}
        </span>
      ) : null}
      <IconButton
        aria-label="Close"
        onClick={onClose}
        sx={{ position: "absolute", right: 8, top: 8 }}
      >
        <CloseIcon />
      </IconButton>
    </DialogTitle>
    {/* Positioned so the step icons' sr-only labels anchor inside the
        scrolling content; otherwise they anchor to the dialog paper and
        stretch it with a blank scroll area below the table. */}
    <DialogContent dividers sx={{ position: "relative" }}>
      {open && (
        <SiteConnectivityView
          siteId={siteId}
          canRun={canRun}
          cameraIds={cameraIds}
          streamTestAvailable={streamTestAvailable}
          // Watch each camera live, as our servers receive it, or open its
          // web page from our network (ENG-2470).
          cameraAction={(camera) => (
            <div className="flex flex-wrap gap-1">
              <WatchLiveButton
                cameraId={camera.camera_id}
                cameraName={camera.camera_name}
                siteId={siteId}
                variant="quiet"
              />
              <DeviceSessionButton
                available={deviceSessionAvailable}
                siteId={siteId}
                cameraId={camera.camera_id}
                cameraName={camera.camera_name}
                openDeviceTarget={camera.open_device_target}
                variant="quiet"
              />
            </div>
          )}
        />
      )}
    </DialogContent>
  </Dialog>
);

const OnboardingCheck = ({
  siteId,
  synced,
  syncedAt,
  canRun,
  onDone,
  streamTestAvailable,
}: {
  siteId: number;
  synced: boolean;
  syncedAt: string | null;
  canRun: boolean;
  onDone: () => void;
  streamTestAvailable?: boolean | null;
}) => {
  const since = syncedAt ? Date.parse(syncedAt) : Date.now();
  const [now, setNow] = React.useState(Date.now());
  React.useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 60 * 1000);
    return () => clearInterval(timer);
  }, []);
  const refreshing = synced && now - since < ONBOARDING_REFRESH_WINDOW_MS;
  return (
    <section
      className="bg-white border border-[#d1d5db] border-t-4 border-t-actuate-orange rounded-md p-5 mb-3"
      aria-label="Check your cameras"
      data-testid="onboarding-connectivity"
    >
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div>
          <h2 className="m-0 font-mulish font-extrabold text-[20px] text-actuate-blue">
            Last step: check your cameras
          </h2>
          <p className="m-0 mt-1 font-mulish text-[14px] text-[#4c5257] max-w-[80ch]">
            {synced
              ? "Your site is synced. As the connector starts, it reports on every camera: which ones send video, and for the rest, why not and how to fix it. Reports usually arrive within 5 minutes."
              : "Sync the site to start it. Once the connector starts, we check every camera here and tell you which ones send video, and how to fix the rest."}
          </p>
          {refreshing && (
            <p className="m-0 mt-1 font-mulish text-[12px] text-[#6b7075]">
              Updating every 30 seconds for the next few minutes.
            </p>
          )}
        </div>
        <TsButton
          variant="quiet"
          onClick={onDone}
          data-testid="onboarding-connectivity-done"
        >
          Done
        </TsButton>
      </div>
      {synced && (
        <SiteConnectivityView
          siteId={siteId}
          canRun={canRun}
          buttonLabel="Run full camera test"
          connectorRefreshMs={refreshing ? ONBOARDING_REFRESH_MS : null}
          prominent
          streamTestAvailable={streamTestAvailable}
        />
      )}
    </section>
  );
};

export const SiteConnectivitySection = ({
  siteId,
  siteName,
  canRun,
  siteDeployed,
  deployedDate,
  streamTestAvailable,
  deviceSessionAvailable,
  serverOpenDeviceAvailable,
  integrationType,
}: {
  siteId: number;
  siteName?: string | null;
  canRun: boolean;
  /** The site About's `stream_test_available` (ENG-2470). */
  streamTestAvailable?: boolean | null;
  /** The site About's `device_session_available` (ENG-2470 Phase 2). */
  deviceSessionAvailable?: boolean | null;
  /** The site About's `server_open_device_available`: "Open NVR" (ENG-2507). */
  serverOpenDeviceAvailable?: boolean | null;
  /** For the server button's name (NVR vs recorder). */
  integrationType?: string | null;
  /** Settings are synced (false = unsynced changes or never deployed). */
  siteDeployed: boolean;
  /** When the site's settings were last deployed, if ever. */
  deployedDate?: number | string | null;
}) => {
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [onboarding, setOnboarding] = React.useState(() =>
    getOnboardingEntry(siteId)
  );
  // Re-read after a sync: handleSyncSite marks the entry and flips siteDeployed.
  React.useEffect(() => {
    setOnboarding(getOnboardingEntry(siteId));
  }, [siteId, siteDeployed, deployedDate]);
  const { load } = useSiteConnectivity(onboarding ? null : siteId);

  if (onboarding) {
    const synced = Boolean(
      onboarding.syncedAt || (siteDeployed && deployedDate)
    );
    return (
      <OnboardingCheck
        siteId={siteId}
        synced={synced}
        syncedAt={onboarding.syncedAt}
        canRun={canRun}
        streamTestAvailable={streamTestAvailable}
        onDone={() => {
          finishOnboarding(siteId);
          setOnboarding(null);
        }}
      />
    );
  }

  const result = load.status === "ok" ? load.data : null;
  const tone = result ? summaryTone(result.summary) : null;
  const problem = tone === "bad" || tone === "warn";
  const family = familyOf(result);
  const openLabel =
    canRun && dryRunSupported(family) ? "Test cameras" : "Camera check";
  // The site's NVR / recorder, for cameras reached through it (ENG-2507).
  const serverButton = (
    <ServerDeviceSessionButton
      available={serverOpenDeviceAvailable}
      siteId={siteId}
      siteName={siteName}
      integrationType={integrationType}
    />
  );

  return (
    <>
      {problem && result ? (
        <div
          className={`flex flex-wrap items-center justify-between gap-3 rounded-md border px-4 py-3 mb-2 ${
            tone === "bad"
              ? "border-[#b3261e] bg-[#fdecec]"
              : "border-actuate-orange bg-[#fff4e5]"
          }`}
          role="alert"
          data-testid="connectivity-banner"
          data-tone={tone}
        >
          <div className="flex items-start gap-3">
            <WarningAmberRoundedIcon
              sx={{ color: tone === "bad" ? "#b3261e" : "#b25e00", mt: "2px" }}
            />
            <div>
              <p
                className={`m-0 font-mulish font-extrabold text-actuate-blue ${
                  tone === "bad" ? "text-[18px]" : "text-[15px]"
                }`}
              >
                {summarySentence(result.summary, family)}
                {tone === "bad"
                  ? ": this site won’t detect anything until this is fixed."
                  : ""}
              </p>
              <p className="m-0 font-mulish text-[13px] text-[#4c5257]">
                {summaryDetails(result.summary, family).join(" · ")}. See why
                and how to fix it.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {serverButton}
            <TsButton
              onClick={() => setDialogOpen(true)}
              data-testid="connectivity-banner-open"
            >
              See what&apos;s wrong
            </TsButton>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-end gap-3 mb-2">
          {result && result.summary.total > 0 && (
            <span
              className="font-mulish text-[13px] text-[#4c5257]"
              data-testid="connectivity-compact"
            >
              {summarySentence(result.summary, family)}
            </span>
          )}
          {serverButton}
          <TsButton
            variant="outline"
            size="sm"
            onClick={() => setDialogOpen(true)}
            data-testid="open-camera-check"
          >
            {openLabel}
          </TsButton>
        </div>
      )}
      <SiteConnectivityDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        siteId={siteId}
        siteName={siteName}
        canRun={canRun}
        streamTestAvailable={streamTestAvailable}
        deviceSessionAvailable={deviceSessionAvailable}
      />
    </>
  );
};
